// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { CombinedState } from 'reducers';
import moment from 'moment';
import { Col, Row } from 'antd/lib/grid';
import Card from 'antd/lib/card';
import Text from 'antd/lib/typography/Text';
import Tag from 'antd/lib/tag';
import Dropdown from 'antd/lib/dropdown';
import Icon from '@ant-design/icons';
import {
    BorderOutlined, IssuesCloseOutlined, MoreOutlined, ProjectOutlined,
} from '@ant-design/icons/lib/icons';
import { DurationIcon, FramesIcon } from 'icons';
import {
    Job, JobType, Task, User,
} from 'cvat-core-wrapper';
import {
    GetJobStageChinese, GetJobStateChinese,
} from 'cvat-core/src/enums';
import UserSelector from 'components/task-page/user-selector';
import JobActionsMenu from './job-actions-menu';

interface Props {
    job: Job;
    task: Task;
    user: User;
    onJobUpdate: (job: Job) => void;
}

function JobItem(props: Props): JSX.Element {
    const {
        job, task, user, onJobUpdate,
    } = props;
    const { id } = job;
    const created = moment(job.createdDate);
    const updated = moment(job.updatedDate);
    const now = moment(moment.now());
    const deletes = useSelector((cs: CombinedState) => cs.jobs.activities.deletes);
    const deleted = id in deletes ? deletes[id] === true : false;
    const style = {};
    if (deleted) {
        (style as any).pointerEvents = 'none';
        (style as any).opacity = 0.5;
    }
    const frameCountPercent = ((job.frameCount / (task.size || 1)) * 100).toFixed(0);
    const frameCountPercentRepresentation = frameCountPercent === '0' ? '<1' : frameCountPercent;
    return (
        <Col span={24}>
            <Card className='cvat-job-item' style={{ ...style }} data-row-id={job.id}>
                <Row>
                    <Col span={7}>
                        <Row>
                            <Col>
                                <Link
                                    to={job.locked ? '#x' : `/tasks/${job.taskId}/jobs/${job.id}`}
                                    className={job.locked ? 'cvat-job-item-disabled' : ''}
                                >
                                    {`作业 #${job.id}`}
                                </Link>
                            </Col>
                            {
                                job.type === JobType.GROUND_TRUTH && (
                                    <Col offset={1}>
                                        <Tag color='#ED9C00'>Ground truth</Tag>
                                    </Col>
                                )
                            }
                        </Row>
                        <Row className='cvat-job-item-dates-info'>
                            <Col>
                                <Text>创建时间 </Text>
                                <Text type='secondary'>{`${created.format('YYYY-MM-DD HH:mm:ss')}`}</Text>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Text>更新时间 </Text>
                                <Text type='secondary'>{`${updated.format('YYYY-MM-DD HH:mm:ss')}`}</Text>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={7}>
                        <Row className='cvat-job-item-selects' justify='space-between'>
                            <Col>
                                <Row>
                                    <Col className='cvat-job-item-select'>
                                        <Row>
                                            <Text>标注人员：</Text>
                                        </Row>
                                        <UserSelector
                                            className='cvat-job-assignee-selector'
                                            value={job.assignee}
                                            onSelect={(selectedUser: User | null): void => {
                                                job.assignee = selectedUser;
                                                onJobUpdate(job);
                                            }}
                                        />
                                    </Col>
                                    <Col className='cvat-job-item-select'>
                                        <Row>
                                            <Text>审核人员：</Text>
                                        </Row>
                                        <UserSelector
                                            className='cvat-job-auditor-selector'
                                            value={job.auditor}
                                            onSelect={(selectedUser: User | null): void => {
                                                job.auditor = selectedUser;
                                                onJobUpdate(job);
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={10}>
                        <Row className='cvat-job-item-details'>
                            <Col>
                                <Row>
                                    <Col>
                                        <ProjectOutlined />
                                        <Text>作业状态 </Text>
                                        <Text type='secondary' className='cvat-job-item-state'>
                                            {GetJobStageChinese(job.stage)}
                                            {GetJobStateChinese(job.state)}
                                        </Text>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Icon component={DurationIcon} />
                                        <Text>持续时间 </Text>
                                        <Text type='secondary'>{`${moment.duration(now.diff(created)).locale('zh_cn').humanize()}`}</Text>
                                    </Col>
                                </Row>
                                {
                                    job.type !== JobType.GROUND_TRUTH && (
                                        <Row>
                                            <Col>
                                                <Icon component={FramesIcon} />
                                                <Text>帧的范围 </Text>
                                                <Text type='secondary' className='cvat-job-item-frame-range'>
                                                    {`${job.startFrame}-${job.stopFrame}`}
                                                </Text>
                                            </Col>
                                        </Row>
                                    )
                                }
                                <Row>
                                    <Col>
                                        <BorderOutlined />
                                        <Text>帧的数量 </Text>
                                        <Text type='secondary' className='cvat-job-item-frames'>
                                            {`${job.frameCount} (${frameCountPercentRepresentation}%)`}
                                        </Text>
                                    </Col>
                                </Row>
                            </Col>
                            <Col offset={2}>
                                <Row>
                                    <Col>
                                        <IssuesCloseOutlined />
                                        <Text>已解决问题 </Text>
                                        <Text type='secondary' className='cvat-job-item-frames'>
                                            {`${job.resolvedIssuesCount}`}
                                        </Text>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <IssuesCloseOutlined />
                                        <Text>未解决问题 </Text>
                                        <Text type='secondary' className='cvat-job-item-frames'>
                                            {`${job.issuesCount - job.resolvedIssuesCount}`}
                                        </Text>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Dropdown overlay={<JobActionsMenu job={job} user={user} onJobUpdate={onJobUpdate} />}>
                    <MoreOutlined className='cvat-job-item-more-button' />
                </Dropdown>
            </Card>
        </Col>
    );
}

export default React.memo(JobItem);
